/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect } from 'react-router'
import type { CreateAdminRoutesOptions } from '@/features/admin/routes'
import type { AdminViewModel } from '@/features/admin/AdminViewModel'
import { links } from '@/utils/link'

/**
 * Data returned by {@link createAdminViewLoader}. `null` when no workspace is
 * selected yet, so the authentication screen is showing.
 */
export type AdminViewLoaderData = { vm: AdminViewModel } | null

/**
 * Creates a loader function for the admin view that provides its view model,
 * and redirects users who may not see any admin page.
 *
 * @param options
 */
export function createAdminViewLoader(
  options: CreateAdminRoutesOptions,
): LoaderFunction {
  return async ({ request: { signal } }): Promise<AdminViewLoaderData> => {
    const vm = await acquireAdminViewModel(options, signal)
    if (vm && !vm.canSeeAdminView) {
      throw redirect(links.welcome())
    }

    return vm && { vm }
  }
}

/**
 * Creates a loader function for an admin page that redirects users who may not
 * see it.
 *
 * @param options
 * @param canView Whether the user may see the page.
 */
export function createAdminPageLoader(
  options: CreateAdminRoutesOptions,
  canView: (vm: AdminViewModel) => boolean,
): LoaderFunction {
  return async ({ request: { signal } }) => {
    const vm = await acquireAdminViewModel(options, signal)
    if (vm && !canView(vm)) {
      throw redirect(links.welcome())
    }

    return null
  }
}

/**
 * Acquires the admin view model once a workspace is selected, otherwise
 * returns `null`.
 */
async function acquireAdminViewModel(
  options: CreateAdminRoutesOptions,
  signal: AbortSignal,
) {
  const { workspace } = await options.waitForAuthResolution(signal)
  if (!workspace) {
    return null
  }

  const internal = await options.admin.internal()
  return await internal.acquireAdminViewModel(signal)
}
